import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  HStack,
  Text,
  Image,
} from '@chakra-ui/react';
import { FiLock, FiGlobe } from 'react-icons/fi';
import EventPreviewCard from './EventPreviewCard';
import EventDetailsView from './EventDetailsView';

/**
 * EventDetailsModal
 *
 * Read-only view of an approved event for the owning advisor. Once compliance
 * signs off, the advisor still needs every detail they submitted (title,
 * description, pricing, venue, promotion assets, etc.) so they can publish the
 * event on external platforms like Eventbrite or Luma. This surfaces the exact
 * approved content verbatim — nothing here is editable.
 */
const EventDetailsModal = ({ event, isOpen, onClose }) => {
  const [coverPhoto, setCoverPhoto] = useState(null);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside" size="2xl">
        <ModalOverlay bg="rgba(0,0,0,0.7)" backdropFilter="blur(4px)" />
        <ModalContent bg="#0a0a0a" border="1px solid rgba(255,255,255,0.1)" borderRadius="16px" mx={4} maxW="760px">
          <ModalHeader color="#fafafa" fontSize="md" fontWeight="700" pb={2}>
            <HStack spacing={2} align="center">
              <Box color="rgba(255,255,255,0.5)" display="flex">
                <FiLock size={14} />
              </Box>
              <Text>Event details</Text>
            </HStack>
            <Text fontSize="xs" color="rgba(255,255,255,0.4)" fontWeight="400" mt={1}>
              Compliance-approved content. Use these details to publish on Eventbrite, Luma, or your own channels. This view is read-only.
            </Text>
          </ModalHeader>
          <ModalCloseButton color="#a3a3a3" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} />

          <ModalBody pb={6}>
            {event && (
              <>
                {/* Attendee-facing preview card */}
                <Box mb={6}>
                  <Text
                    fontSize="10px"
                    fontWeight="700"
                    color="rgba(255,255,255,0.22)"
                    letterSpacing="0.09em"
                    textTransform="uppercase"
                    mb={3}
                    textAlign="center"
                  >
                    Typical attendee view · layouts vary by platform
                  </Text>
                  <EventPreviewCard data={event} mode="invite" />
                </Box>

                {/* Publishing intent note — mirrors the reviewer-facing banner */}
                <Flex
                  align="flex-start"
                  gap={2.5}
                  mb={6}
                  px={4}
                  py={3}
                  bg="rgba(99,179,237,0.06)"
                  border="1px solid rgba(99,179,237,0.15)"
                  borderRadius="12px"
                >
                  <Box color="rgba(99,179,237,0.8)" flexShrink={0} mt="2px" display="flex">
                    <FiGlobe size={13} />
                  </Box>
                  <Text fontSize="xs" color="rgba(255,255,255,0.55)" lineHeight="1.7">
                    These are the approved materials, exactly as compliance signed off. Publish
                    them as-is — the GillyReach regulatory disclosure is automatically appended to
                    the event description when promoted.
                  </Text>
                </Flex>

                {/* Full read-only details */}
                <EventDetailsView event={event} onViewCover={setCoverPhoto} />
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Cover photo lightbox — shows the whole, uncropped image. */}
      <Modal isOpen={!!coverPhoto} onClose={() => setCoverPhoto(null)} isCentered size="4xl">
        <ModalOverlay bg="rgba(0,0,0,0.85)" backdropFilter="blur(4px)" />
        <ModalContent bg="transparent" boxShadow="none" mx={4} my="auto">
          <ModalCloseButton
            color="#fafafa"
            bg="rgba(0,0,0,0.45)"
            borderRadius="full"
            zIndex={2}
            _hover={{ bg: 'rgba(0,0,0,0.7)' }}
            _focus={{ boxShadow: 'none' }}
          />
          <Flex align="center" justify="center">
            <Image
              src={coverPhoto || undefined}
              alt="Event cover — full size"
              maxH="85vh"
              maxW="100%"
              objectFit="contain"
              borderRadius="10px"
            />
          </Flex>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EventDetailsModal;
